import React from 'react';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { toast } from 'sonner';

import TriEventsLoadingScreen from '../../components/common/TriEventsLoadingScreen';
import BackButton from '../../components/common/BackButton';

// Shadcn Components
import { Card, CardContent, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../components/ui/tooltip";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { cn } from '../../lib/utils';

// Icons
import { Download, Users, Search, RefreshCw } from 'lucide-react';

// Hooks
import { useTenantNavigation } from '../../hooks/navigation/useTenantNavigation';
import { useAttendees } from '../../hooks/event/useAttendees';
import { attendeeService } from '../../services/event/attendeeService';
import { Attendee } from '../../types/event/attendee';
import {DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger} from '@/components/ui/dropdown-menu';

const Attendees: React.FC = () => {
    const { uuid } = useParams<{ uuid: string }>();
    const { getTenantPath } = useTenantNavigation();
    const { attendees, loading, error, handleExportCSV, refreshAttendees } = useAttendees(uuid!);
    const [searchQuery, setSearchQuery] = React.useState('');
    const [selectedAttendee, setSelectedAttendee] = React.useState<Attendee | null>(null);
    const [newEmail, setNewEmail] = React.useState('');
    const [isReissuing, setIsReissuing] = React.useState(false);
    const [isDialogOpen, setIsDialogOpen] = React.useState(false);
    const [isRefundDialogOpen, setIsRefundDialogOpen] = React.useState(false);
    const [isRefunding, setIsRefunding] = React.useState(false);

    const handleReissueTicket = async () => {
        if (!selectedAttendee) return;
        
        try {
            setIsReissuing(true);
            await attendeeService.reissueTicket(selectedAttendee.uuid, newEmail);
            toast.success("Ticket has been reissued successfully");
            setSelectedAttendee(null);
            setNewEmail('');
            setIsDialogOpen(false);
            // Refresh the attendees list to show the updated email
            await refreshAttendees();
        } catch (error) {
            toast.error(error instanceof Error ? error.message : "Failed to reissue ticket");
        } finally {
            setIsReissuing(false);
        }
    };

    const handleRefundTicket = async () => {
        if (!selectedAttendee) return;

        try {
            setIsRefunding(true);
            const response = await attendeeService.refundTicket(selectedAttendee.uuid);
            setSelectedAttendee(null);
            setIsRefundDialogOpen(false);
            await refreshAttendees();
            if (!response.ok) {
                if (response.message === "No associated transaction") {
                    toast.error("Could not refund, please contact support@tricode.cloud");
                }
                else {
                    toast.error("Could not refund, please try again");
                }
            }
            else {
                toast.success("Ticket refunded!")
            }
        } catch (error) {
            toast.error("Could not refund, please try again");
        } finally {
            setIsRefunding(false);
        }
    }

    const filteredAttendees = React.useMemo(() => {
        if (!searchQuery.trim()) return attendees;
        const query = searchQuery.toLowerCase();
        return attendees.filter(attendee => 
            attendee.name.toLowerCase().includes(query)
        );
    }, [attendees, searchQuery]);

    if (loading) {
        return <TriEventsLoadingScreen loading={loading} />;
    }

    if (error) {
        return (
            <section className="min-h-screen bg-background">
                <div className="container mx-auto px-4 py-8">
                    <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                </div>
            </section>
        );
    }

    return (
        <section className="bg-background">
            <div className="container mx-auto pb-12">
                {/* Reissue Dialog - Moved outside table structure to avoid stacking context issues */}
                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Reissue Ticket</DialogTitle>
                            <DialogDescription>
                                Enter the new email address to reissue the ticket to.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="py-4">
                            <Input
                                type="email"
                                placeholder="New email address"
                                value={newEmail}
                                onChange={(e) => setNewEmail(e.target.value)}
                            />
                        </div>
                        <DialogFooter className="gap-4">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setSelectedAttendee(null);
                                    setNewEmail('');
                                    setIsDialogOpen(false);
                                }}
                                className="order-1 md:order-2"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleReissueTicket}
                                disabled={isReissuing || !newEmail}
                                className="order-2 md:order-1"
                            >
                                {isReissuing ? 'Reissuing...' : 'Reissue Ticket'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
                <Dialog open={isRefundDialogOpen} onOpenChange={setIsRefundDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Refund Confirmation</DialogTitle>
                            <DialogDescription>
                                <p>Are you sure you want to refund the ticket issued to {selectedAttendee?.email}?</p>
                                <p>(Note: The service fee will not be refunded to your organization)</p>
                            </DialogDescription>
                        </DialogHeader>
                        <DialogFooter className="gap-4">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setSelectedAttendee(null);
                                    setIsRefundDialogOpen(false);
                                }}
                                className="order-1 md:order-2"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleRefundTicket}
                                disabled={isRefunding}
                                className="order-2 md:order-1"
                            >
                                {isRefunding ? 'Refunding...' : 'Refund'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
                <div>
                    <div className="mb-0 md:mb-6">
                        <BackButton to={getTenantPath(`/event/${uuid}`)} text="Back to Event" />
                    </div>

                    <Card className="border-none bg-[#23d6e4] text-white">
                        <CardContent className="p-6">
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                                <div>
                                    <CardTitle className="text-3xl font-bold mb-2 text-white">
                                        Event Attendees
                                    </CardTitle>
                                    <CardDescription className="text-white/80">
                                        View and manage event attendees
                                    </CardDescription>
                                </div>
                                <div className="flex flex-col sm:flex-row gap-3">
                                    <Badge variant="secondary" className="h-10 px-4 text-base whitespace-nowrap">
                                        {attendees.length} {attendees.length === 1 ? 'Attendee' : 'Attendees'}
                                    </Badge>
                                    <Button
                                        onClick={handleExportCSV}
                                        size="lg"
                                        className="w-full md:w-auto gap-3 text-lg transition-transform duration-200 hover:scale-105"
                                    >
                                        <Download className="h-6 w-6 stroke-[3.5]" />
                                        Export CSV
                                    </Button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <div className="mt-6">
                        {attendees.length === 0 ? (
                            <Card>
                                <CardContent className="p-6">
                                    <div className="text-center py-8">
                                        <Users className="mx-auto h-12 w-12 text-muted-foreground" />
                                        <h3 className="mt-4 text-lg font-semibold">No attendees found</h3>
                                        <p className="mt-2 text-sm text-muted-foreground">
                                            There are no attendees for this event yet.
                                        </p>
                                    </div>
                                </CardContent>
                            </Card>
                        ) : (
                            <div className="space-y-4">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        placeholder="Search attendees by name..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="pl-9"
                                    />
                                </div>

                                {/* Mobile View */}
                                <div className="md:hidden space-y-4">
                                    {filteredAttendees.map((attendee, index) => (
                                        <Card key={index}>
                                            <CardContent className="p-6">
                                                <div className="space-y-4">
                                                    <div className="flex justify-between items-start gap-4">
                                                        <div className="min-w-0 flex-1">
                                                            <TooltipProvider>
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <p className="font-medium truncate text-base">
                                                                            {attendee.name}
                                                                        </p>
                                                                    </TooltipTrigger>
                                                                    <TooltipContent>
                                                                        {attendee.name}
                                                                    </TooltipContent>
                                                                </Tooltip>
                                                            </TooltipProvider>
                                                            <TooltipProvider>
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <p className="text-sm text-muted-foreground truncate mt-1">
                                                                            {attendee.email}
                                                                        </p>
                                                                    </TooltipTrigger>
                                                                    <TooltipContent>
                                                                        {attendee.email}
                                                                    </TooltipContent>
                                                                </Tooltip>
                                                            </TooltipProvider>
                                                        </div>
                                                        <div className="flex flex-col items-end gap-2">
                                                            <Badge
                                                                variant="outline"
                                                                className={cn(
                                                                    "rounded-full text-xs whitespace-nowrap min-w-[65px] flex items-center justify-center",
                                                                    attendee.ticket_burned 
                                                                        ? "bg-red-100 text-red-800 border-red-200" 
                                                                        : "bg-green-100 text-green-800 border-green-200"
                                                                )}
                                                            >
                                                                {attendee.ticket_burned ? 'Used' : 'Unused'}
                                                            </Badge>
                                                            {!attendee.ticket_burned && (
                                                                <DropdownMenu>
                                                                    <DropdownMenuTrigger asChild>
                                                                        <Button
                                                                            variant="outline"
                                                                            size="sm"
                                                                            className="gap-2"
                                                                        >
                                                                            Actions
                                                                        </Button>
                                                                    </DropdownMenuTrigger>
                                                                    <DropdownMenuContent>
                                                                        <DropdownMenuItem className="cursor-pointer" onClick={() =>  {
                                                                            setSelectedAttendee(attendee);
                                                                            setIsDialogOpen(true);
                                                                        }}>
                                                                            Reissue
                                                                        </DropdownMenuItem>
                                                                        <DropdownMenuItem className="cursor-pointer" onClick={() => {
                                                                            setSelectedAttendee(attendee);
                                                                            setIsRefundDialogOpen(true);
                                                                        }}>
                                                                            Refund
                                                                        </DropdownMenuItem>
                                                                    </DropdownMenuContent>
                                                                </DropdownMenu>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="space-y-1 text-sm text-muted-foreground">
                                                        <p>Phone: {attendee.phone || '-'}</p>
                                                        <p>Ticket: {attendee.ticket_type}</p>
                                                        <p>Purchased: {format(new Date(attendee.purchase_date), 'MMM d, yyyy')}</p>
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>

                                {/* Desktop View */}
                                <div className="hidden md:block w-full">
                                    <div className="border rounded-lg overflow-hidden w-full">
                                        <div className="min-h-[500px] max-h-[500px] overflow-auto w-full bg-gray-50">
                                            <div className="grid w-full bg-white" style={{ 
                                                gridTemplateColumns: 'minmax(140px, 1.5fr) minmax(140px, 2fr) minmax(100px, 1.2fr) minmax(80px, 1fr) minmax(80px, 1fr) minmax(100px, 1fr)'
                                            }}>
                                                {/* Header */}
                                                <div className="sticky top-0 bg-black z-10 border-b py-2 px-4 font-medium text-white truncate">Name</div>
                                                <div className="sticky top-0 bg-black z-10 border-b py-2 px-4 font-medium text-white text-center truncate">Email</div>
                                                <div className="sticky top-0 bg-black z-10 border-b py-2 px-4 font-medium text-white text-center truncate">Phone</div>
                                                <div className="sticky top-0 bg-black z-10 border-b py-2 px-4 font-medium text-white text-center truncate">Ticket Type</div>
                                                <div className="sticky top-0 bg-black z-10 border-b py-2 px-4 font-medium text-white text-center truncate">Status</div>
                                                <div className="sticky top-0 bg-black z-10 border-b py-2 px-4 font-medium text-white text-center truncate">Actions</div>

                                                {/* Rows */}
                                                {filteredAttendees.map((attendee, index) => (
                                                    <React.Fragment key={index}>
                                                        <div className="border-b py-2 px-4 flex items-center min-h-[48px]">
                                                            <div className="min-w-0 space-y-0.5">
                                                                <TooltipProvider>
                                                                    <Tooltip>
                                                                        <TooltipTrigger asChild>
                                                                            <p className="font-medium truncate text-sm">
                                                                                {attendee.name}
                                                                            </p>
                                                                        </TooltipTrigger>
                                                                        <TooltipContent>
                                                                            {attendee.name}
                                                                        </TooltipContent>
                                                                    </Tooltip>
                                                                </TooltipProvider>
                                                            </div>
                                                        </div>
                                                        <div className="border-b py-2 px-4 flex items-center min-h-[48px]">
                                                            <TooltipProvider>
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <p className="truncate text-sm w-full text-center">{attendee.email}</p>
                                                                    </TooltipTrigger>
                                                                    <TooltipContent>
                                                                        {attendee.email}
                                                                    </TooltipContent>
                                                                </Tooltip>
                                                            </TooltipProvider>
                                                        </div>
                                                        <div className="border-b py-2 px-4 flex items-center min-h-[48px]">
                                                            <p className="text-sm w-full text-center">{attendee.phone || '-'}</p>
                                                        </div>
                                                        <div className="border-b py-2 px-4 flex items-center min-h-[48px]">
                                                            <p className="text-sm w-full text-center">{attendee.ticket_type}</p>
                                                        </div>
                                                        <div className="border-b py-2 px-4 flex items-center justify-center min-h-[48px]">
                                                            <Badge
                                                                variant="outline"
                                                                className={cn(
                                                                    "rounded-full text-xs whitespace-nowrap min-w-[65px] flex items-center justify-center",
                                                                    attendee.ticket_burned 
                                                                        ? "bg-red-100 text-red-800 border-red-200" 
                                                                        : "bg-green-100 text-green-800 border-green-200"
                                                                )}
                                                            >
                                                                {attendee.ticket_burned ? 'Used' : 'Unused'}
                                                            </Badge>
                                                        </div>
                                                        <div className="border-b py-2 px-4 flex items-center justify-center min-h-[48px]">
                                                            {!attendee.ticket_burned && (
                                                                <DropdownMenu>
                                                                    <DropdownMenuTrigger asChild>
                                                                        <Button
                                                                            variant="outline"
                                                                        >
                                                                            Actions
                                                                        </Button>
                                                                    </DropdownMenuTrigger>
                                                                    <DropdownMenuContent>
                                                                        <DropdownMenuItem className="cursor-pointer" onClick={() =>  {
                                                                            setSelectedAttendee(attendee);
                                                                            setIsDialogOpen(true);
                                                                        }}>
                                                                            Reissue
                                                                        </DropdownMenuItem>
                                                                        <DropdownMenuItem className="cursor-pointer" onClick={() => {
                                                                            setSelectedAttendee(attendee);
                                                                            setIsRefundDialogOpen(true);
                                                                        }}>
                                                                            Refund
                                                                        </DropdownMenuItem>
                                                                    </DropdownMenuContent>
                                                                </DropdownMenu>
                                                            )}
                                                        </div>
                                                    </React.Fragment>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Attendees;